import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { ESPatientService, PatientData, CCMStatusUpdate, CCMStatusOption, GroupOption } from '@/services/es/esPatientService.ts';

// Simple session storage for group persistence
const STORAGE_KEY = 'es_selected_group';
const LOCKED_PATIENT_KEY = 'es_locked_patient';

export const saveGroupSelection = (userId: string, groupId: number, groupName: string) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, groupId, groupName, timestamp: Date.now() }));
  } catch (e) {
    console.warn('Failed to save group selection');
  }
};

export const loadGroupSelection = (userId: string): { groupId: number; groupName: string } | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    if (data.userId === userId && Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
      return { groupId: data.groupId, groupName: data.groupName };
    }
  } catch (e) {
    console.warn('Failed to load group selection');
  }
  return null;
};

// Save locked patient information for refresh scenarios
export const saveLockedPatient = (userId: string, patientId: number) => {
  try {
    sessionStorage.setItem(LOCKED_PATIENT_KEY, JSON.stringify({ userId, patientId, timestamp: Date.now() }));
  } catch (e) {
    console.warn('Failed to save locked patient info');
  }
};

// Load locked patient information
export const loadLockedPatient = (userId: string): number | null => {
  try {
    const stored = sessionStorage.getItem(LOCKED_PATIENT_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored);
    if (data.userId === userId && Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
      return data.patientId;
    }
  } catch (e) {
    console.warn('Failed to load locked patient info');
  }
  return null;
};

// Clear locked patient information
export const clearLockedPatient = () => {
  sessionStorage.removeItem(LOCKED_PATIENT_KEY);
};

export { STORAGE_KEY, LOCKED_PATIENT_KEY };

export const useESPatientData = (
  externalPatientId?: number | null, 
  onExternalPatientLoaded?: () => void,
  externalActivityId?: number | null,
  externalWorkflowId?: number | null
) => {
  const { user, token, hasuraToken } = useAuth();
  const { toast } = useToast();

  // Create a stable service instance ref to prevent recreation
  const serviceRef = useRef<ESPatientService | null>(null);

  // Cache keys to prevent duplicate API calls
  const patientDataCacheKeyRef = useRef<string | null>(null);
  const initialLoadRequestedRef = useRef(false);
  const initialLoadCompletedRef = useRef(false);

  // Service instance - use useMemo to prevent recreation on every render
  const esService = useMemo(() => {
    if (user?.id && !serviceRef.current) {
      serviceRef.current = new ESPatientService(user.id, hasuraToken, user.type, token);
    }
    return serviceRef.current;
  }, [user?.id, hasuraToken, user?.type, token]);

  // Update auth context when tokens change - log to diagnose issues
  useEffect(() => {
    if (esService && hasuraToken && user?.type && token) {
      esService.updateAuthContext(hasuraToken, user.type, token);
    }
  }, [esService, hasuraToken, user?.type, token]);

  // Diagnostic effect to track re-renders
  useEffect(() => {
    return () => {
    };
  }, []);

  // State management
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [ccmStatusUpdates, setCCMStatusUpdates] = useState<CCMStatusUpdate[]>([]);
  const [rpmStatusUpdates, setRPMStatusUpdates] = useState<any[]>([]);
  const [ccmStatusOptions, setCcmStatusOptions] = useState<CCMStatusOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [clinicNumbers, setClinicNumbers] = useState<any[]>([]);
  const [clinicNumbersSources, setClinicNumbersSources] = useState<any[]>([]);
  const [smsClinicNumbers, setSmsClinicNumbers] = useState<any[]>([]);
  const [smsNumbersSources, setSmsNumbersSources] = useState<any[]>([]);
  const [esNamesMapping, setESNamesMapping] = useState<Record<number, string>>({});

  const [patientClinicDataId, setPatientClinicDataId] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [patientTimezone, setPatientTimezone] = useState<string>('US/Eastern');
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const [assignmentData, setAssignmentData] = useState<any>(null);

  // Simple persistent group selection state
  const [persistentGroupId, setPersistentGroupId] = useState<number | null>(null);
  const [persistentGroupName, setPersistentGroupName] = useState<string>('');
  const [hasPersistedGroup, setHasPersistedGroup] = useState(false);

  // Track initial login and group switches for conditional locking
  const [isInitialLogin, setIsInitialLogin] = useState(true);
  const [lastLockedGroupId, setLastLockedGroupId] = useState<number | null>(null);
  const [currentPatientId, setCurrentPatientId] = useState<number | null>(null);
  const [hasHandledRefreshUnlock, setHasHandledRefreshUnlock] = useState(false);

  // Initialize persistent group from session storage
  useEffect(() => {
    if (user?.id && groupOptions.length > 0) {
      const stored = loadGroupSelection(user.id);
      if (stored) {
        // Validate that stored group still exists
        const groupExists = groupOptions.some(g => g.group_id === stored.groupId);
        if (groupExists) {
          setPersistentGroupId(stored.groupId);
          setPersistentGroupName(stored.groupName);
          setHasPersistedGroup(true);
        } else {
          // Stored group no longer exists, clear it and show alert
          console.warn(`Stored group ${stored.groupId} (${stored.groupName}) no longer exists in available options`);
          clearPersistentGroup();
          
          // Log invalid stored group (no alert needed - will be handled by group switch popup)
          console.log(`Stored group "${stored.groupName}" is no longer available - will switch to first available group`);
        }
      }
    }
  }, [user?.id, groupOptions]);

  // Functions for managing persistent group selection
  const setPersistentGroup = (groupId: number, groupName: string) => {
    setPersistentGroupId(groupId);
    setPersistentGroupName(groupName);
    setHasPersistedGroup(true);
    if (user?.id) {
      saveGroupSelection(user.id, groupId, groupName);
    }
  };

  const clearPersistentGroup = () => {
    setPersistentGroupId(null);
    setPersistentGroupName('');
    setHasPersistedGroup(false);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingGroupChange, setIsLoadingGroupChange] = useState(false);
  const [isDeferredMode, setIsDeferredMode] = useState(false);
  const [hasDeferredTask, setHasDeferredTask] = useState(false);
  const [hasEmptyPatientList, setHasEmptyPatientList] = useState(false);
  const [noPatientMessage, setNoPatientMessage] = useState<string | null>(null);

  // Sequential loading states
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
  const [isLoadingCCMData, setIsLoadingCCMData] = useState(false);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [patientDataLoaded, setPatientDataLoaded] = useState(false);
  const [ccmDataLoaded, setCCMDataLoaded] = useState(false);

  // Editable form fields state
  const [editableFields, setEditableFields] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    gender: '',
    mobile_phone_number: '',
    home_phone_number: '',
    email: '',
    address_line_1: '',
    city: '',
    state: '',
    zip_code: '',
    clinic_name: '',
    provider_name: '',
    primary_dx: '',
    secondary_dx: '',
    primary_insurance_provider: '',
    primary_insurance_id: '',
    secondary_insurance_provider: '',
    secondary_insurance_id: ''
  });

  // Initialize editable fields when patient data changes
  const initializeEditableFields = (data: PatientData) => {
    setEditableFields({
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      dob: data.dob || '',
      gender: data.gender || '',
      mobile_phone_number: data.mobile_phone_number || '',
      home_phone_number: data.home_phone_number || '',
      email: data.email || '',
      address_line_1: data.address_line_1 || '',
      city: data.city || '',
      state: data.state || '',
      zip_code: data.zip_code || '',
      clinic_name: data.clinic_name || '',
      provider_name: data.provider_name || '',
      primary_dx: data.primary_dx || '',
      secondary_dx: data.secondary_dx || '',
      primary_insurance_provider: data.primary_insurance_provider || '',
      primary_insurance_id: data.primary_insurance_id || '',
      secondary_insurance_provider: data.secondary_insurance_provider || '',
      secondary_insurance_id: data.secondary_insurance_id || ''
    });
  };

  // Complete form reset functionality
  const resetFormData = () => {
    // Reset patient data
    setPatientData(null);
    setCCMStatusUpdates([]);
    setRPMStatusUpdates([]);
    setClinicNumbers([]);
    setSmsClinicNumbers([]);
    setPatientClinicDataId(null);
    setGroupId(null);
    setSelectedGroup('');
    
    // Reset form fields
    setEditableFields({
      first_name: '',
      last_name: '',
      dob: '',
      gender: '',
      mobile_phone_number: '',
      home_phone_number: '',
      email: '',
      address_line_1: '',
      city: '',
      state: '',
      zip_code: '',
      clinic_name: '',
      provider_name: '',
      primary_dx: '',
      secondary_dx: '',
      primary_insurance_provider: '',
      primary_insurance_id: '',
      secondary_insurance_provider: '',
      secondary_insurance_id: ''
    });
    
    // Reset all sequential loading states
    setIsLoadingPatientData(false);
    setIsLoadingCCMData(false);
    setPatientDataLoaded(false);
    setCCMDataLoaded(false);
    
    // Reset view state flags - important for preventing incorrect UI states
    patientDataCacheKeyRef.current = null;
  };

  // Trigger sequential loading for next patient
  const triggerSequentialLoading = () => {
    setPatientDataLoaded(false);
    setCCMDataLoaded(false);
    setIsLoadingPatientData(true);
    setIsLoadingCCMData(false);
  };

  // Complete patient data loading phase
  const completePatientDataLoading = () => {
    setIsLoadingPatientData(false);
    setPatientDataLoaded(true);
    setIsLoadingCCMData(true);
  };

  // Complete CCM data loading phase
  const completeCCMDataLoading = () => {
    setIsLoadingCCMData(false);
    setCCMDataLoaded(true);
  };

  /**
   * Helper function to check if a group has patients using workflow assignments
   * Since workflow assignments are user-specific, we just return true for all groups
   * The actual patient availability is determined by the workflow assignment query
   */
  const checkGroupHasPatients = async (groupId: number): Promise<boolean> => {
    // Simplified: assume all groups in groupOptions have potential patients
    // The workflow assignment query will handle filtering by assigned_user_id
    return true;
  };

  // Find next available group with patients
  const findNextAvailableGroup = async (currentGroupId: number): Promise<{ group_id: number; group_name: string } | null> => {
    if (!esService) return null;

    try {
      // Get current group index
      const currentGroupIndex = groupOptions.findIndex(group => group.group_id === currentGroupId);

      // Try each subsequent group
      for (let i = currentGroupIndex + 1; i < groupOptions.length; i++) {
        const group = groupOptions[i];
        const hasPatients = await checkGroupHasPatients(group.group_id);

        if (hasPatients) {
          return group;
        }
      }

      // If no subsequent groups have patients, try groups before current
      for (let i = 0; i < currentGroupIndex; i++) {
        const group = groupOptions[i];
        const hasPatients = await checkGroupHasPatients(group.group_id);

        if (hasPatients) {
          return group;
        }
      }

      return null; // No groups have patients
    } catch (error) {
      console.error('Error finding next available group:', error);
      return null;
    }
  };

  // Helper function to conditionally lock patient by group
  const conditionalLockPatientByGroup = async (groupId: number, isGroupChange: boolean = false): Promise<boolean> => {
    if (!esService || !user?.id) return false;

    try {
      // Check for previously locked patient from session storage (for refresh scenarios only)
      if (isInitialLogin && !hasHandledRefreshUnlock) {
        const previouslyLockedPatient = loadLockedPatient(user.id);
        if (previouslyLockedPatient) {
          console.log(`Unlocking previously locked patient ${previouslyLockedPatient} from refresh/reload`);
          await esService.unlockPatient(previouslyLockedPatient);
          clearLockedPatient();
          setHasHandledRefreshUnlock(true);
        }
      }

      // Only unlock current patient if this is an actual group change
      if (isGroupChange && currentPatientId) {
        console.log(`Unlocking previous patient ${currentPatientId} due to group change`);
        await esService.unlockPatient(currentPatientId);
        clearLockedPatient(); // Clear from session storage
      }

      // Only lock if it's initial login or actual group has changed
      const shouldLock = isInitialLogin || (isGroupChange && lastLockedGroupId !== groupId);
      
      if (shouldLock) {
        console.log(`Locking patient by group ${groupId} - Initial login: ${isInitialLogin}, Group changed: ${isGroupChange && lastLockedGroupId !== groupId}`);
        const lockSuccess = await esService.lockPatientByGroup(groupId);
        
        if (lockSuccess) {
          setLastLockedGroupId(groupId);
          setIsInitialLogin(false); // Mark initial login as complete
        }
        
        return lockSuccess;
      } else {
        console.log(`Skipping lock for group ${groupId} - already locked and no group change`);
        return true; // Return true since we don't need to lock
      }
    } catch (error) {
      console.error('Error in conditional lock patient by group:', error);
      return false;
    }
  };

  // Switch to next available group
  const switchToNextAvailableGroup = async (): Promise<boolean> => {
    if (!esService || !groupId) return false;

    try {
      // Refresh group options first to get latest available groups
      const updatedGroupOptions = await esService.loadGroupOptions();
      setGroupOptions(updatedGroupOptions);

      const nextGroup = await findNextAvailableGroup(groupId);

      if (nextGroup) {
        // Reset form data
        resetFormData();

        // Switch to the new group
        setGroupId(nextGroup.group_id);
        setSelectedGroup(nextGroup.group_id.toString());

        // Load patient from new group using workflow assignments
        // Conditionally lock patient by group (this is a group switch)
        await conditionalLockPatientByGroup(nextGroup.group_id, true);

        // Sequential loading
        setIsLoadingPatientData(true);

        const result = await esService.getPatientData();

        if (result?.patientData && result?.assignmentData) {
          const { patientData, workflowId: wfId } = result;
          // Use enrollmentPatientId from visiblePatient API response
          const pep_patient_clinic_data_id = result.assignmentData.enrollmentPatientId || result.assignmentData.patient_id;
          const activityId = result.assignmentData.activityId || result.assignmentData.activity_id;
          
          setPatientClinicDataId(pep_patient_clinic_data_id);
          setPatientData(patientData);
          setWorkflowId(wfId);
          // Store assignment data with workflowId and activityId
          setAssignmentData({
            ...result.assignmentData,
            workflow_id: wfId || result.assignmentData.workflowId,
            activity_id: activityId
          });
          setPatientTimezone(patientData.patient_clinic_timezone || 'US/Eastern');
          initializeEditableFields(patientData);
          
          // Save locked patient info for refresh scenarios
          if (user?.id && pep_patient_clinic_data_id) {
            saveLockedPatient(user.id, pep_patient_clinic_data_id);
          }

          // Load clinic numbers if available (Call and SMS separately via REST API)
          if (patientData.clinic_id) {
            try {
              // Load call numbers
              const callNums = await esService.loadCallNumbers(patientData.clinic_id);
              setClinicNumbers(callNums);
              const callSources = esService.generateSourceOptions(callNums);
              setClinicNumbersSources(callSources);
              
              // Load SMS numbers
              const smsNums = await esService.loadSmsNumbers(patientData.clinic_id);
              setSmsClinicNumbers(smsNums);
              const smsSources = esService.generateSourceOptions(smsNums);
              // Add "All" option for SMS
              if (!smsSources.find(s => s.number_source_id === 0)) {
                smsSources.unshift({ number_source: 'All', number_source_id: 0 });
              }
              setSmsNumbersSources(smsSources);
            } catch (error) {
              console.error('Error loading clinic/SMS numbers:', error);
            }
          }

          setIsLoadingPatientData(false);
          setPatientDataLoaded(true);

          // Load CCM data
          setIsLoadingCCMData(true);
          
          // Extract enrollment attempts from patient data (already included from REST API)
          if (result?.patientData?.ccm_status_updates) {
            setCCMStatusUpdates(result.patientData.ccm_status_updates);
          }
          if (result?.patientData?.rpm_status_updates) {
            setRPMStatusUpdates(result.patientData.rpm_status_updates);
          }
          
          setIsLoadingCCMData(false);
          setCCMDataLoaded(true);

          return true;
        }
      }

      return false; // No available groups with patients
    } catch (error) {
      console.error('Error switching to next available group:', error);
      return false;
    }
  };

  // Load specific patient by patient_id (for external navigation)
  const loadSpecificPatient = async (patientId: number) => {
    if (!esService) return;

    console.log('[useESPatientData] loadSpecificPatient called with patientId:', patientId);

    try {
      setIsLoading(true);
      setIsLoadingGroups(true);
      
      // Load initial dropdown options first
      const [esNames, groupOpts, deferredPermission] = await Promise.all([
        esService.getESNamesMapping(),
        esService.loadGroupOptions(),
        esService.checkDeferredTaskPermission()
      ]);

      setESNamesMapping(esNames);
      setGroupOptions(groupOpts);
      setHasDeferredTask(deferredPermission);
      setIsLoadingGroups(false);
      setGroupsLoaded(true);
      
      // Now load the specific patient - pass userType to skip assignment query for non-ES users
      setIsLoadingPatientData(true);
      const result = await esService.getSpecificPatientData(patientId, user?.type);
      
      console.log('[useESPatientData] getSpecificPatientData result:', {
        hasPatientData: !!result?.patientData,
        hasAssignmentData: !!result?.assignmentData,
        workflowId: result?.workflowId,
        clinicId: result?.patientData?.clinic_id
      });
      
      if (result?.patientData) {
        const { patientData, workflowId: wfId, assignmentData } = result;
        setPatientClinicDataId(patientId);
        
        // Set group data only if assignmentData exists (for workflow users)
        if (assignmentData) {
          setGroupId(assignmentData.userGroupId || assignmentData.user_group_id);
          setSelectedGroup((assignmentData.userGroupId || assignmentData.user_group_id)?.toString() || '');
          setAssignmentData(assignmentData);
        } else if (externalActivityId || externalWorkflowId) {
          // Use external IDs when assignment data is not available (CS/PE users)
          setAssignmentData({
            activity_id: externalActivityId,
            workflow_id: externalWorkflowId
          });
        }
        
        setPatientData(patientData);
        // Use API workflow ID first, fall back to external workflow ID
        setWorkflowId(wfId || externalWorkflowId || null);
        setPatientTimezone(patientData.patient_clinic_timezone || 'US/Eastern');
        initializeEditableFields(patientData);
        setCurrentPatientId(patientId);
        
        console.log('[useESPatientData] Patient data loaded, clinic_id:', patientData.clinic_id);
        
        if (patientData.clinic_id) {
          console.log('[useESPatientData] Loading clinic numbers for clinic:', patientData.clinic_id);
          const callNums = await esService.loadCallNumbers(patientData.clinic_id);
          setClinicNumbers(callNums);
          setClinicNumbersSources(esService.generateSourceOptions(callNums));
          
          const smsNums = await esService.loadSmsNumbers(patientData.clinic_id);
          setSmsClinicNumbers(smsNums);
          const smsSources = esService.generateSourceOptions(smsNums);
          if (!smsSources.find(s => s.number_source_id === 0)) {
            smsSources.unshift({ number_source: 'All', number_source_id: 0 });
          }
          setSmsNumbersSources(smsSources);
          console.log('[useESPatientData] Clinic numbers loaded successfully');
        } else {
          console.warn('[useESPatientData] No clinic_id found in patient data');
        }
        
        if (result?.patientData?.ccm_status_updates) {
          setCCMStatusUpdates(result.patientData.ccm_status_updates);
        }
        if (result?.patientData?.rpm_status_updates) {
          setRPMStatusUpdates(result.patientData.rpm_status_updates);
        }
        
        console.log('[useESPatientData] All patient data loaded successfully');
      }
      
      setIsLoadingPatientData(false);
      setPatientDataLoaded(true);
      setIsLoadingCCMData(false);
      setCCMDataLoaded(true);
      setIsLoading(false);
      
      if (onExternalPatientLoaded) {
        onExternalPatientLoaded();
      }
    } catch (error) {
      console.error('[useESPatientData] Error loading specific patient:', error);
      setIsLoadingPatientData(false);
      setIsLoading(false);
      toast({
        title: "Error",
        description: "Failed to load patient data. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Load initial data with sequential loading and persistent group selection
  const loadInitialData = async () => {
    if (!esService) return;

    try {
      setIsLoading(true);
      setIsLoadingGroups(true);

      // Phase 1: FIRST call GetESPatientData (patient workflow assignments query)
      // This ensures the GraphQL query for patient assignment is called before all other APIs
      console.log('[loadInitialData] Step 1: Calling GetESPatientData (patient workflow assignments)');
      const patientResult = await esService.getPatientData();
      
      if (!patientResult?.patientData || !patientResult?.assignmentData) {
        // No patient assignment found - load group options to show empty state
        console.log('[loadInitialData] No patient assignment found, loading group options only');
        
        // Set the message from API if available
        if (patientResult?.noPatientMessage) {
          setNoPatientMessage(patientResult.noPatientMessage);
          // Show empty state with API message
          setHasEmptyPatientList(true);
          setPatientData(null);
          setPatientClinicDataId(null);
        }
        
        const groupOpts = await esService.loadGroupOptions();
        setGroupOptions(groupOpts);
        
        if (!groupOpts || groupOpts.length === 0) {
          setHasEmptyPatientList(true);
          setPatientData(null);
          setPatientClinicDataId(null);
          setGroupId(null);
          setSelectedGroup('');
          clearPersistentGroup();
        }
        
        setIsLoadingGroups(false);
        setGroupsLoaded(true);
        setIsLoadingPatientData(false);
        setIsLoadingCCMData(false);
        setIsLoading(false);
        return;
      }
      
      // Clear no patient message when patient is found
      setNoPatientMessage(null);

      // Phase 2: Load remaining APIs in parallel (after patient workflow assignment is fetched)
      console.log('[loadInitialData] Step 2: Loading remaining APIs (ES names, group options, deferred permission)');
      const [esNames, groupOpts, deferredPermission] = await Promise.all([
        esService.getESNamesMapping(),
        esService.loadGroupOptions(),
        esService.checkDeferredTaskPermission()
      ]);

      setESNamesMapping(esNames);
      setGroupOptions(groupOpts);
      // CCM status options removed - no longer needed
      // Number sources are now generated dynamically from actual numbers loaded per patient
      setClinicNumbersSources([]);
      setSmsNumbersSources([]);
      setHasDeferredTask(deferredPermission);
      setIsLoadingGroups(false);
      setGroupsLoaded(true);

      // Phase 3: Process patient data (already fetched in Phase 1)
      console.log('[loadInitialData] Step 3: Processing patient data from workflow assignment');
      setIsLoadingPatientData(true);

      try {
        const { patientData, workflowId: wfId, assignmentData: asgnData } = patientResult;
        // Use enrollmentPatientId from visiblePatient API response (new field name)
        const patientId = patientResult.assignmentData.enrollmentPatientId || patientResult.assignmentData.patient_id;
        const groupId = patientResult.assignmentData.userGroupId || patientResult.assignmentData.user_group_id;
        const activityId = patientResult.assignmentData.activityId || patientResult.assignmentData.activity_id;
        
        console.log('[loadInitialData] Patient data received from workflow assignments:', {
          patientId,
          groupId,
          workflowId: wfId,
          activityId,
          hasEnrollmentAttempts: !!patientData.enrollmentAttemptsByProgramType,
          enrollmentKeys: patientData.enrollmentAttemptsByProgramType ? Object.keys(patientData.enrollmentAttemptsByProgramType) : []
        });

        setPatientClinicDataId(patientId);
        setGroupId(groupId);
        setSelectedGroup(groupId?.toString() || '');
        setHasEmptyPatientList(false);

        // Conditionally lock patient by group (this is initial login)
        if (groupId) {
          await conditionalLockPatientByGroup(groupId, false);
        }

        setPatientData(patientData);
        setWorkflowId(wfId);
        // Store full assignment data including workflowId and activityId from visiblePatient
        setAssignmentData({
          ...asgnData,
          workflow_id: wfId || asgnData?.workflowId,
          activity_id: activityId
        });
        setPatientTimezone(patientData.patient_clinic_timezone || 'US/Eastern');
        initializeEditableFields(patientData);
        
        // Track current patient ID for unlock functionality
        setCurrentPatientId(patientId);
        
        // Save locked patient info for refresh scenarios
        if (user?.id && patientId) {
          saveLockedPatient(user.id, patientId);
        }

        // Load clinic numbers if available (Call and SMS separately via REST API)
        if (patientData.clinic_id) {
          try {
            // Load call numbers
            const callNums = await esService.loadCallNumbers(patientData.clinic_id);
            setClinicNumbers(callNums);
            const callSources = esService.generateSourceOptions(callNums);
            setClinicNumbersSources(callSources);
            
            // Load SMS numbers
            const smsNums = await esService.loadSmsNumbers(patientData.clinic_id);
            setSmsClinicNumbers(smsNums);
            const smsSources = esService.generateSourceOptions(smsNums);
            // Add "All" option for SMS
            if (!smsSources.find(s => s.number_source_id === 0)) {
              smsSources.unshift({ number_source: 'All', number_source_id: 0 });
            }
            setSmsNumbersSources(smsSources);
          } catch (error) {
            console.error('Error loading clinic/SMS numbers:', error);
          }
        }

        setIsLoadingPatientData(false);
        setPatientDataLoaded(true);

        // Phase 4: Load CCM data
        setIsLoadingCCMData(true);

        // Extract enrollment attempts from patient data (already included from REST API)
        if (patientResult?.patientData?.ccm_status_updates) {
          setCCMStatusUpdates(patientResult.patientData.ccm_status_updates);
        }
        if (patientResult?.patientData?.rpm_status_updates) {
          setRPMStatusUpdates(patientResult.patientData.rpm_status_updates);
        }

        setIsLoadingCCMData(false);
        setCCMDataLoaded(true);
      } catch (error) {
        console.error('Error in patient data loading:', error);
        // Clear stored group on error and show empty state
        sessionStorage.removeItem(STORAGE_KEY);
        setHasEmptyPatientList(true);
        setIsLoadingPatientData(false);
        setIsLoadingCCMData(false);
        
        toast({
          title: "Error",
          description: "Failed to load patient data. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      // Don't show error toast if it's an authentication error (will be handled by apiService)
      if (error?.response?.status !== 401) {
        toast({
          title: "Error",
          description: "Failed to load patient data. Please try refreshing the page.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch patient data - optimized to prevent duplicate calls
  const refetchPatientData = async (specificPatientId?: number) => {
    if (!esService) return;

    const patientId = specificPatientId || patientClinicDataId;
    if (!patientId) return;

    // Generate a cache key based on the patient ID and a timestamp within a reasonable window (5 seconds)
    const now = Math.floor(Date.now() / 5000);
    const cacheKey = `${patientId}-${now}`;

    // If we've already fetched this patient data in the same time window, skip
    if (patientDataCacheKeyRef.current === cacheKey) {
      return;
    }

    patientDataCacheKeyRef.current = cacheKey;

    try {
      // Fetch patient data which now includes enrollment attempts from REST API
      const result = await esService.getPatientData();

      if (result?.patientData) {
        const { patientData: fetchedPatientData, workflowId: wfId } = result;
        setPatientData(fetchedPatientData);
        setWorkflowId(wfId);
        setPatientTimezone(fetchedPatientData.patient_clinic_timezone || 'US/Eastern');
        initializeEditableFields(fetchedPatientData);

        // Extract enrollment attempts from the fetched patient data (from REST API)
        // The REST API returns enrollmentAttemptsByProgramType which is already transformed
        if (fetchedPatientData.ccm_status_updates) {
          setCCMStatusUpdates(fetchedPatientData.ccm_status_updates);
        }
        if (fetchedPatientData.rpm_status_updates) {
          setRPMStatusUpdates(fetchedPatientData.rpm_status_updates);
        }

        // Load clinic numbers if available (Call and SMS separately via REST API)
        if (fetchedPatientData.clinic_id) {
          try {
            // Load call numbers
            const callNums = await esService.loadCallNumbers(fetchedPatientData.clinic_id);
            setClinicNumbers(callNums);
            const callSources = esService.generateSourceOptions(callNums);
            setClinicNumbersSources(callSources);
            
            // Load SMS numbers
            const smsNums = await esService.loadSmsNumbers(fetchedPatientData.clinic_id);
            setSmsClinicNumbers(smsNums);
            const smsSources = esService.generateSourceOptions(smsNums);
            // Add "All" option for SMS
            if (!smsSources.find(s => s.number_source_id === 0)) {
              smsSources.unshift({ number_source: 'All', number_source_id: 0 });
            }
            setSmsNumbersSources(smsSources);
          } catch (error) {
            console.error('Error loading clinic/SMS numbers:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error refetching patient data:', error);
      toast({
        title: "Error",
        description: "Failed to refresh patient data",
        variant: "destructive"
      });
    }
  };

  // Refetch ONLY enrollment details (no assignment query) - for post-save refresh
  const refetchEnrollmentDetailsOnly = async () => {
    if (!esService || !patientClinicDataId) return;

    try {
      console.log('[useESPatientData] Refetching enrollment details only (no assignment query)');
      const fetchedPatientData = await esService.getEnrollmentDetailsOnly(patientClinicDataId);

      if (fetchedPatientData) {
        // Update only enrollment-related data, keep existing patient data structure
        setPatientData(prevData => ({
          ...prevData!,
          ...fetchedPatientData
        }));

        // Extract enrollment attempts from the fetched patient data (from REST API)
        if (fetchedPatientData.ccm_status_updates) {
          setCCMStatusUpdates(fetchedPatientData.ccm_status_updates);
        }
        if (fetchedPatientData.rpm_status_updates) {
          setRPMStatusUpdates(fetchedPatientData.rpm_status_updates);
        }
      }
    } catch (error) {
      console.error('Error refetching enrollment details only:', error);
      toast({
        title: "Error",
        description: "Failed to refresh enrollment details",
        variant: "destructive"
      });
    }
  };

  // Load deferred patient
  const loadDeferredPatient = async () => {
    if (!esService) return;

    try {
      setIsLoading(true);
      
      // Reset state before loading to prevent stale data
      setPatientData(null);
      setCCMStatusUpdates([]);
      setRPMStatusUpdates([]);
      
      // Reset form state completely when switching to deferred mode
      resetFormData();
      
      // Set deferred mode first to ensure correct component rendering path
      setIsDeferredMode(true);
      
      // Important: Set hasEmptyPatientList to false initially
      // This prevents the "Congrats" message from showing prematurely
      setHasEmptyPatientList(false);
      
      const deferredResult = await esService.loadDeferredPatient();

      if (deferredResult && deferredResult.patientClinicDataId) {
        const { patientClinicDataId: deferredId, patientData, ccmUpdates } = deferredResult;

        // Set patient data and related state
        setPatientClinicDataId(deferredId);
        
        // Set sequential loading states to indicate data is loaded
        setPatientDataLoaded(true);
        setCCMDataLoaded(true);
        
        if (patientData) {
          setPatientData(patientData);
          setPatientTimezone(patientData.patient_clinic_timezone || 'US/Eastern');
          initializeEditableFields(patientData);

          // Extract enrollment attempts from patient data (already included from REST API)
          const ccmUpdates = patientData.ccm_status_updates || [];
          const rpmUpdates = patientData.rpm_status_updates || [];
          setCCMStatusUpdates(ccmUpdates);
          setRPMStatusUpdates(rpmUpdates);

          // Load clinic numbers if available (Call and SMS separately via REST API)
          if (patientData.clinic_id) {
            try {
              // Load call numbers
              const callNums = await esService.loadCallNumbers(patientData.clinic_id);
              setClinicNumbers(callNums);
              const callSources = esService.generateSourceOptions(callNums);
              setClinicNumbersSources(callSources);
              
              // Load SMS numbers
              const smsNums = await esService.loadSmsNumbers(patientData.clinic_id);
              setSmsClinicNumbers(smsNums);
              const smsSources = esService.generateSourceOptions(smsNums);
              // Add "All" option for SMS
              if (!smsSources.find(s => s.number_source_id === 0)) {
                smsSources.unshift({ number_source: 'All', number_source_id: 0 });
              }
              setSmsNumbersSources(smsSources);
            } catch (error) {
              console.error('Error loading clinic/SMS numbers:', error);
            }
          }
        }
      } else {
        // No deferred patients available - show completion message
        // Only set this to true when we're certain there's no data
        setHasEmptyPatientList(true);
      }
    } catch (error) {
      console.error('Error loading deferred patient:', error);
      toast({
        title: "Error",
        description: "Failed to load deferred patient",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back to normal patients
  const handleBackToNormalPatients = async () => {
    if (!esService) return;

    try {
      // First, reset all state to ensure clean transition
      setIsLoading(true);
      
      // Explicitly reset deferred mode and empty patient list flags
      setIsDeferredMode(false);
      setHasEmptyPatientList(false);
      
      // Reset patient data and related state
      setPatientClinicDataId(null);
      setPatientDataLoaded(false);
      setCCMDataLoaded(false);
      
      // Reset form data completely - this will unlock the form
      resetFormData();
      
      // Reload initial data to get fresh state
      await loadInitialData();
    } catch (error) {
      console.error('Error switching back to normal patients:', error);
      toast({
        title: "Error",
        description: "Failed to switch back to normal patients",
        variant: "destructive"
      });
    }
  };

  // Handle field changes
  const handleFieldChange = (fieldName: keyof typeof editableFields, value: string) => {
    setEditableFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Initialize on mount - using refs to ensure it only runs once
  useEffect(() => {
    // Early return if no auth or if already loaded
    if (!esService || !hasuraToken || !user?.id || !token) {
      console.log('[useESPatientData] Waiting for auth context:', {
        hasService: !!esService,
        hasHasuraToken: !!hasuraToken,
        hasUserId: !!user?.id,
        hasToken: !!token
      });
      return;
    }

    // CRITICAL: If externalPatientId is provided, ONLY load that specific patient
    // Do NOT query by assigned_user_id, do NOT call loadInitialData
    if (externalPatientId) {
      console.log('[useESPatientData] External patient ID provided - loading specific patient only:', externalPatientId);
      
      // Skip normal initialization flow completely
      if (!initialLoadRequestedRef.current) {
        initialLoadRequestedRef.current = true;
        
        // Load ONLY the specific patient data - no user assignment queries
        loadSpecificPatient(externalPatientId);
        initialLoadCompletedRef.current = true;
      }
      
      // Early return to prevent loadInitialData from being called
      return;
    }

    // Regular flow: load data for assigned patients (only when no externalPatientId)
    if (initialLoadRequestedRef.current) {
      return;
    }

    // Wait for persistent group selection to initialize first
    // Add a small delay to ensure the persistent group hook has time to initialize
    const timeoutId = setTimeout(() => {
      initialLoadRequestedRef.current = true;

      // Use a separate function to avoid dependency issues
      const runInitialLoad = async () => {
        try {
          await loadInitialData();
          initialLoadCompletedRef.current = true;
        } catch (error) {
          // Reset the flag to allow retry on next mount
          initialLoadRequestedRef.current = false;
        }
      };

      runInitialLoad();
    }, 100); // Small delay to let persistent group selection initialize

    // Return cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, [esService, hasuraToken, user?.id, token, externalPatientId]); // Dependencies for auth check

  return {
    // Data
    patientData,
    ccmStatusUpdates,
    rpmStatusUpdates,
    ccmStatusOptions,
    groupOptions,
    clinicNumbers,
    clinicNumbersSources,
    smsClinicNumbers,
    smsNumbersSources,
    editableFields,
    esNamesMapping,

    // State
    patientClinicDataId,
    groupId,
    selectedGroup,
    patientTimezone,
    workflowId,
    assignmentData,
    isLoading,
    isLoadingGroupChange,
    isDeferredMode,
    hasDeferredTask,
    hasEmptyPatientList,
    noPatientMessage,

    // Sequential loading states
    isLoadingGroups,
    isLoadingPatientData,
    isLoadingCCMData,
    groupsLoaded,
    patientDataLoaded,
    ccmDataLoaded,

    // Actions
    refetchPatientData,
    refetchEnrollmentDetailsOnly,
    loadDeferredPatient,
    handleBackToNormalPatients,
    handleFieldChange,
    initializeEditableFields,
    resetFormData,
    triggerSequentialLoading,
    completePatientDataLoading,
    completeCCMDataLoading,
    findNextAvailableGroup,
    switchToNextAvailableGroup,

    // Setters for external use
    setPatientClinicDataId,
    setGroupId,
    setSelectedGroup,
    setCCMStatusUpdates,
    setRPMStatusUpdates,
    setPatientData,
    setEditableFields,
    setIsLoadingGroupChange,
    setHasEmptyPatientList,
    setClinicNumbers,
    setClinicNumbersSources,
    setSmsClinicNumbers,
    setSmsNumbersSources,

    // Persistent group selection
    setPersistentGroup,
    clearPersistentGroup,
    hasPersistedGroup,

    // Service instance for operations
    esService,

    // Conditional locking functionality
    conditionalLockPatientByGroup,
    isInitialLogin,
    lastLockedGroupId,
    currentPatientId,
    setCurrentPatientId
  };
};